import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Search,
  Users,
  DollarSign,
  Star,
  MoreVertical,
  Loader,
  Eye,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  XCircle,
  Plus,
  Grid3x3,
  List,
  ChevronLeft,
  ChevronRight,
  Filter,
  Award,
} from "lucide-react";
import { coursesApi, showSuccess, showError } from "../lib";

type ViewMode = "grid" | "list";

const AdminCoursesPage = () => {
  const [courses, setCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<any | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0,
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchCourses();
    fetchStats();
  }, [currentPage, statusFilter]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const result = await coursesApi.getAllCourses({
        page: currentPage,
        limit: 12,
        status: (statusFilter as any) || undefined,
      });

      if (result.success) {
        setCourses(result.courses || result.data);
        setPagination({
          total: result.pagination?.total || 0,
          pages: result.pagination?.pages || 1,
        });
      }
    } catch (err: any) {
      showError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const result = await coursesApi.getStats();
      if (result.success) {
        setStats({
          total: result.stats?.totalCourses || 0,
          published: result.stats?.publishedCourses || 0,
          draft: result.stats?.draftCourses || 0,
          archived: result.stats?.archivedCourses || 0,
        });
      }
    } catch (err: any) {
      console.error("Failed to load stats");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCourses();
  };

  const handleTogglePublish = async (course: any) => {
    setActionLoading(true);
    try {
      const newStatus = course.status === "published" ? "draft" : "published";
      await coursesApi.updateCourse(course._id, { status: newStatus });
      showSuccess(`Course ${newStatus === "published" ? "published" : "unpublished"} successfully`);
      fetchCourses();
      fetchStats();
    } catch (err: any) {
      showError(err.message || "Failed to update course");
    } finally {
      setActionLoading(false);
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const filteredCourses = courses.filter((course) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const instructorName = course.instructor?.name || course.instructorName || "";
    return (
      course.title?.toLowerCase().includes(query) ||
      course.description?.toLowerCase().includes(query) ||
      instructorName.toLowerCase().includes(query)
    );
  });

  // Grid View Render
  const renderGridView = () => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {filteredCourses.map((course) => (
        <div
          key={course._id}
          className="overflow-hidden transition-all duration-200 bg-white border border-gray-100 rounded-xl hover:shadow-md"
        >
          <div className="relative h-32 bg-gradient-to-br from-amber-100 to-orange-100">
            {course.thumbnail ? (
              <img
                src={course.thumbnail}
                alt={course.title}
                className="object-cover w-full h-full"
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full">
                <BookOpen className="w-10 h-10 text-amber-300" />
              </div>
            )}
            <span
              className={`absolute top-2 right-2 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                course.status === "published"
                  ? "bg-green-100 text-green-700"
                  : course.status === "draft"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {course.status}
            </span>
          </div>
          <div className="p-3">
            <h6 className="mb-1 text-xs font-bold text-gray-900 line-clamp-1">
              {course.title}
            </h6>
            <p className="text-[9px] text-gray-500 line-clamp-2 mb-2">
              {truncateText(course.description || "No description", 80)}
            </p>

            <div className="flex items-center gap-3 mb-2 text-[9px] text-gray-500">
              <span className="flex items-center gap-0.5">
                <Users className="w-2.5 h-2.5" />
                {course.enrolledStudents || 0}
              </span>
              <span className="flex items-center gap-0.5">
                <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                {course.rating || 0}
              </span>
              <span className="flex items-center gap-0.5">
                <DollarSign className="w-2.5 h-2.5" />
                {course.price || 0} XAF
              </span>
            </div>

            <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
              <Link
                to={`/course/${course._id}`}
                target="_blank"
                className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-600 transition rounded-lg hover:bg-gray-100"
              >
                <Eye className="w-2.5 h-2.5" /> View
              </Link>
              <Link
                to={`/dashboard/instructor/courses/${course._id}/edit`}
                className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-600 transition rounded-lg hover:bg-gray-100"
              >
                <Edit className="w-2.5 h-2.5" /> Edit
              </Link>
              <button
                onClick={() => handleTogglePublish(course)}
                disabled={actionLoading}
                className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-600 transition rounded-lg hover:bg-gray-100"
              >
                {course.status === "published" ? (
                  <>
                    <ToggleLeft className="w-2.5 h-2.5" /> Unpublish
                  </>
                ) : (
                  <>
                    <ToggleRight className="w-2.5 h-2.5" /> Publish
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // List View Render - Mobile Responsive
  const renderListView = () => (
    <div className="space-y-2">
      {filteredCourses.map((course) => (
        <div
          key={course._id}
          className="p-3 transition-all bg-white border border-gray-100 rounded-lg hover:shadow-sm"
        >
          <div className="flex gap-2">
            <div className="flex-shrink-0 w-16 h-16 overflow-hidden rounded-lg bg-gradient-to-br from-amber-100 to-orange-100">
              {course.thumbnail ? (
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  <BookOpen className="w-6 h-6 text-amber-300" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h6 className="text-xs font-bold text-gray-900 line-clamp-1">
                  {course.title}
                </h6>
                <span
                  className={`shrink-0 text-[8px] font-medium px-1.5 py-0.5 rounded-full ${
                    course.status === "published"
                      ? "bg-green-100 text-green-700"
                      : course.status === "draft"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {course.status}
                </span>
              </div>
              <p className="text-[9px] text-gray-500 line-clamp-2 mb-1.5">
                {truncateText(course.description || "No description", 60)}
              </p>
              <div className="flex items-center gap-2 mb-1.5 text-[8px] text-gray-500">
                <span className="flex items-center gap-0.5">
                  <Users className="w-2.5 h-2.5" />
                  {course.enrolledStudents || 0}
                </span>
                <span className="flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                  {course.rating || 0}
                </span>
                <span className="flex items-center gap-0.5">
                  <DollarSign className="w-2.5 h-2.5" />
                  {course.price || 0} XAF
                </span>
              </div>
              <p className="text-[8px] text-gray-400">
                 Instructor: {course.instructor?.name || course.instructorName || "Unknown"}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-gray-100">
            <Link
              to={`/course/${course._id}`}
              target="_blank"
              className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-600 transition rounded-lg hover:bg-gray-100"
            >
              <Eye className="w-2.5 h-2.5" /> View
            </Link>
            <Link
              to={`/dashboard/instructor/courses/${course._id}/edit`}
              className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-600 transition rounded-lg hover:bg-gray-100"
            >
              <Edit className="w-2.5 h-2.5" /> Edit
            </Link>
            <button
              onClick={() => handleTogglePublish(course)}
              disabled={actionLoading}
              className="flex items-center gap-1 px-2 py-1 text-[9px] text-gray-600 transition rounded-lg hover:bg-gray-100"
            >
              {course.status === "published" ? (
                <>
                  <ToggleLeft className="w-2.5 h-2.5" /> Unpublish
                </>
              ) : (
                <>
                  <ToggleRight className="w-2.5 h-2.5" /> Publish
                </>
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                  Course Management
                </h1>
              </div>
              <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
                Manage all courses on the platform
              </p>
            </div>
            <Link
              to="/dashboard/admin/create-course"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all w-fit md:text-xs md:px-4 md:py-2"
            >
              <Plus className="w-3.5 h-3.5" /> Create Course
            </Link>
          </div>
        </div>

        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-between mb-1">
              <BookOpen className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-bold text-gray-900">{stats.total}</span>
            </div>
            <p className="text-[9px] text-gray-500">Total Courses</p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-between mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-bold text-gray-900">{stats.published}</span>
            </div>
            <p className="text-[9px] text-gray-500">Published</p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-between mb-1">
              <BookOpen className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-bold text-gray-900">{stats.draft}</span>
            </div>
            <p className="text-[9px] text-gray-500">Draft</p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-between mb-1">
              <XCircle className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-bold text-gray-900">{stats.archived}</span>
            </div>
            <p className="text-[9px] text-gray-500">Archived</p>
          </div>
        </div>

        {/* Filters & View Toggle */}
        <div className="p-3 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl">
          <div className="flex flex-col gap-3">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[150px]">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>
              <div className="w-[120px]">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
                >
                  <option value="">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <button
                type="submit"
                className="px-3 py-1.5 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-[10px] font-medium rounded-lg hover:shadow-md transition-all"
              >
                Search
              </button>
              {(statusFilter || searchQuery) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setStatusFilter("");
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1.5 text-[9px] text-orange-600 hover:text-orange-700"
                >
                  Clear
                </button>
              )}
            </form>

            {/* View Toggle Buttons */}
            <div className="flex items-center justify-end gap-1 p-0.5 bg-gray-100 rounded-lg w-fit">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-medium rounded-md transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-orange-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Grid3x3 className="w-3 h-3" />
                Grid
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-medium rounded-md transition-all ${
                  viewMode === "list"
                    ? "bg-white text-orange-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <List className="w-3 h-3" />
                List
              </button>
            </div>
          </div>
        </div>

        {/* Courses Display */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-100 rounded-xl">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-xs text-gray-500">No courses found</p>
            {(searchQuery || statusFilter) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("");
                }}
                className="mt-2 text-[10px] text-orange-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          viewMode === "grid" ? renderGridView() : renderListView()
        )}

        {/* Pagination */}
        {pagination.pages > 1 && filteredCourses.length > 0 && (
          <div className="flex flex-col items-center gap-3 mt-5 sm:flex-row sm:justify-between">
            <p className="text-[9px] text-gray-500 order-2 sm:order-1">
              Showing {filteredCourses.length} of {pagination.total} courses
            </p>
            <div className="flex items-center order-1 gap-1 sm:order-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
              </button>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-[28px] h-7 rounded-lg text-[10px] font-medium transition-colors border ${
                        currentPage === pageNum
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {pagination.pages > 5 && currentPage < pagination.pages - 2 && (
                  <>
                    <span className="px-1 text-[9px] text-gray-400">...</span>
                    <button
                      onClick={() => setCurrentPage(pagination.pages)}
                      className="min-w-[28px] h-7 rounded-lg text-[10px] font-medium transition-colors border bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    >
                      {pagination.pages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={currentPage === pagination.pages}
                className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoursesPage;